import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import common from "../../common.module.scss";
import style from "../OrderHistory.module.scss";
import styles from "./OrderHistoryMobile.module.scss";
import { numberToFinanceFormat } from "../../../../../utils";
import { CURRENCIES, ORDER_STATUSES_DATA } from "../../../../../mock";
import BallIcon from "../../../../shared/icons/BallIcon";
import LogoIcon from "../../../../shared/icons/LogoIcon";
import LupeIcon from "../../../../shared/icons/LupeIcon";
import UsersIcon from "../../../../shared/icons/UsersIcon";
import OrdersIcon from "../../../../shared/icons/OrdersIcon";
import UncheckCircleIcon from "../../../../shared/icons/UncheckCircleIcon";
import OrdersInfoDirections from "../OrdersInfoDirections";
import OrdersInfoCargo from "../OrdersInfoCargo";
import YandexMap from "../YandexMap";
import OrdersInfoAddressList from "../OrdersInfoAddressList";
import { LanguageDropdown } from "../../../../LanguageSelector/LanguageSelector";
import { useNavigate } from "react-router-dom";
import OrdersDriverInfoShare from "../OrdersDriverInfoShare";
import { prepareYandexMapData } from "../../../../../utils/mapDataTransformers";

const OrderShareContent = ({ item, recipientName, senderName, routeData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const yandexMapData = useMemo(() => prepareYandexMapData(item, routeData), [item, routeData]);

  const getStatusKey = (statusNumber) => {
    const statusMap = {
      0: "moderation",
      1: "awaitingpayment",
      2: "published",
      3: "booked",
      4: "confirmed",
      5: "processloadingcargo",
      6: "loadingcargomoderation",
      7: "rejectedloadingcargomoderation",
      8: "waitstarttrip",
      9: "onthetrip",
      10: "processunloadingcargo",
      11: "unloadingcargomoderation",
      12: "rejectedunloadingcargomoderation",
      13: "completed",
      14: "rejected",
      15: "canceled",
      16: "awaitingoffer",
      17: "assigned",
      18: "assignedexecutor",
      19: "expeditorrejected",
      20: "completedbyexecutor",
    };
    return statusMap[statusNumber] || "unknown";
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };
    return date.toLocaleDateString("ru-RU", options);
  };

  const getCurrentStatus = () => {
    if (!item.orderStates || item.orderStates.length === 0) {
      return getStatusKey(item.status);
    }

    const sortedStates = [...item.orderStates].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return getStatusKey(sortedStates[0].status);
  };

  const currentStatus = getCurrentStatus();

  const getIconColor = (status, step) => {
    const rejectedStatuses = [
      "rejected",
      "canceled",
      "rejectedloadingcargomoderation",
      "rejectedunloadingcargomoderation",
      "expeditorrejected",
    ];

    const currentStep = {
      moderation: 1,
      awaitingpayment: 1,
      rejected: 1,
      expeditorrejected: 1,
      awaitingoffer: 1,
      published: 2,
      booked: 3,
      assigned: 3,
      assignedexecutor: 3,
      confirmed: 3,
      processloadingcargo: 3,
      loadingcargomoderation: 3,
      waitstarttrip: 3,
      rejectedloadingcargomoderation: 3,
      onthetrip: 4,
      processunloadingcargo: 4,
      unloadingcargomoderation: 4,
      rejectedunloadingcargomoderation: 4,
      completed: 5,
      completedbyexecutor: 5,
    };

    if (rejectedStatuses.includes(status) && step === currentStep[status]) {
      return { backgroundColor: "#C84620", iconColor: "#FFF", IconType: 2 };
    } else if (step <= currentStep[status]) {
      return { backgroundColor: "#212121", iconColor: "#FFF", IconType: 2 };
    } else {
      return { backgroundColor: "#F0F0F0", iconColor: "#212121", IconType: 1 };
    }
  };

  const renderIcon = (step) => {
    const status = currentStatus;
    const { backgroundColor, iconColor, IconType } = getIconColor(status, step);

    switch (step) {
      case 1:
        return (
          <>
            <div className={style.history_icons_circle} style={{ backgroundColor }}>
              <div className={style.history_icons_icon}>
                <LogoIcon type={IconType} color={iconColor} width="32px" height="32px" />
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div className={style.history_icons_line} style={{ backgroundColor }}></div>
            <div className={style.history_icons_circle} style={{ backgroundColor }}>
              <div className={style.history_icons_icon}>
                <LupeIcon color={iconColor} />
              </div>
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div className={style.history_icons_line} style={{ backgroundColor }}></div>
            <div className={style.history_icons_circle} style={{ backgroundColor }}>
              <div className={style.history_icons_icon}>
                <UsersIcon color={iconColor} />
              </div>
            </div>
          </>
        );
      case 4:
        return (
          <>
            <div className={style.history_icons_line} style={{ backgroundColor }}></div>
            <div className={style.history_icons_circle} style={{ backgroundColor }}>
              <div className={style.history_icons_icon}>
                <OrdersIcon color={iconColor} />
              </div>
            </div>
          </>
        );
      case 5:
        return (
          <>
            <div className={style.history_icons_line} style={{ backgroundColor }}></div>
            <div className={style.history_icons_circle} style={{ backgroundColor }}>
              <div className={style.history_icons_icon}>
                <UncheckCircleIcon color={iconColor} />
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const setSummary = (status) => {
    switch (status) {
      case "moderation":
        return t("orderInfo.orderHistory.summaryModeration");
      case "published":
        return "";
      case "awaitingpayment":
        return "";
      case "awaitingoffer":
        return "";
      case "assigned":
        return "";
      case "assignedexecutor":
        return "";
      case "expeditorrejected":
        return "";
      case "waitstarttrip":
        return "";
      case "onthetrip":
        return t("orderInfo.orderHistory.summaryOnTheTrip");
      case "processloadingcargo":
        return t("orderInfo.orderHistory.summaryProcessLoading");
      case "loadingcargomoderation":
        return "";
      case "rejectedloadingcargomoderation":
        return "";
      case "processunloadingcargo":
        return t("orderInfo.orderHistory.summaryProcessUnloading");
      case "unloadingcargomoderation":
        return "";
      case "rejectedunloadingcargomoderation":
        return "";
      case "completed":
        return "";
      case "completedbyexecutor":
        return "";
      case "canceled":
        return "";
      case "booked":
        return `${item.driverInfo?.value?.surname} ${item.driverInfo?.value?.name}`;
      case "rejected":
        return "";
      case "confirmed":
        return "";
      default:
        return "";
    }
  };

  return (
    <div className={cn(styles.mobile__wrapper)}>
      <div className={styles.mobile__modal} style={{ minHeight: "100vh" }}>
        <div className={styles.topbar}>
          <div className={styles.topbar__contentShare}>
            <div className={styles.topbar__title}>
              <div className={styles.card_logo} onClick={() => navigate("/profile")}>
                <img src="/icons/ic_biny_dark.svg" alt="logo" />
              </div>
              <div className={style.history_title}>Просмотр заказа</div>
            </div>
            <LanguageDropdown />
          </div>
        </div>

        <div className={styles.mobile__main} style={{ flexDirection: "row", paddingBottom: "30px" }}>
          <div className={style.history_content}>
            <div className={style.history_status}>
              <div className={style.history_status_text}>
                <div
                  className={style.history_status_title}
                  style={{ color: ORDER_STATUSES_DATA(t)[currentStatus]?.color }}
                >
                  <BallIcon fill={ORDER_STATUSES_DATA(t)[currentStatus]?.color} />
                  {ORDER_STATUSES_DATA(t)[currentStatus]?.title || currentStatus}
                </div>
                <div className={style.history_status_summary}>{setSummary(currentStatus)}</div>
              </div>

              <div className={style.history_icons}>
                {renderIcon(1)}
                {renderIcon(2)}
                {renderIcon(3)}
                {renderIcon(4)}
                {renderIcon(5)}
              </div>
            </div>

            <div className={style.history_details}>
              <div className={style.history_details_line}>
                <BallIcon fill="#6D7885" />
                <div className={style.history_details_border}></div>
                <BallIcon fill="#6D7885" />
              </div>
              <div className={style.history_details_info}>
                {item.orderStates
                  ?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                  ?.map((orderState, index) => {
                    const statusKey = getStatusKey(orderState.status);
                    const statusData = ORDER_STATUSES_DATA(t)[statusKey];

                    return (
                      <div key={orderState.id || index} className={style.history_details_item}>
                        <div className={style.history_details_title}>
                          {statusData?.title || `Status ${orderState.status}`}
                        </div>
                        <div className={style.history_details_summary}>
                          {formatDate(orderState.createdAt)}
                          {orderState.moderator && <span> — {orderState.moderator}</span>}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* {driverPhotos && driverPhotos.length > 0 ? <ListDocsOrder documents={driverPhotos} /> : null} */}

            {item && item.order?.addressList.length < 1 ? (
              <OrdersInfoDirections item={item.order} senderName={senderName} recipientName={recipientName} />
            ) : (
              <OrdersInfoAddressList order={item.order} addressList={item.order?.addressList} />
            )}

            <div className={cn(common.basic_wrapper, styles.mobile__basic)}>
              <div className={common.basic_numbers}>
                <span className={common.basic_price}>
                  {numberToFinanceFormat(item.order?.price)}{" "}
                  {CURRENCIES[item.order?.currency || "KZT"]?.symbol || ""}
                </span>
                <div className={common.basic_lower_part}>
                  <span className={common.basic_number}> №{item.order?.number}</span>
                  {item?.referenceNumber && <span className={common.basic_number}>- {item?.referenceNumber}</span>}
                </div>
              </div>
              <div className={common.basic_distance}>
                {numberToFinanceFormat(item.order?.distance)}
                {t("analytics.distance_metric")}
              </div>
            </div>

            {item && item.driverInfo?.value && <OrdersDriverInfoShare item={item.driverInfo?.value} vehicle={item?.vehicle}/>}

            <OrdersInfoCargo item={item?.cargo} order={item?.order} />
          </div>
          <div className={style.history_maps}>
            <YandexMap
              // item={yandexMapData.item}
              addressList={yandexMapData.addressList}
              cityFrom={yandexMapData.cityFrom}
              cityTo={yandexMapData.cityTo}
              routeData={yandexMapData.routeData}
              coordinateTruck={yandexMapData.coordinateTruck}
              // isLoadingMap={isLoadingMap}
            />
          </div>
        </div>
      </div>
      <button className={style.history_btn}></button>
    </div>
  );
};

export default OrderShareContent;
